import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useAppSelector } from '../../store/store';
import axios from '../../axios';
import { playerService } from '../../services/player';
import { getImageAnalysis2 } from '../../utils/imageAnyliser';
import { MdSettings } from 'react-icons/md';
import './style.css';

interface LyricLine {
  timeMs: number | null;
  text: string;
  romanizedText?: string;
}

interface LyricsPayload {
  synced: boolean;
  hasRomanized?: boolean;
  lines: LyricLine[];
}

const lyricsCache: Record<string, LyricsPayload> = {};

const EqualizerIcon = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="currentColor"
    aria-hidden="true"
    style={{ display: 'inline-block', flexShrink: 0 }}
  >
    <rect x="2" y="8" width="3" height="8" rx="1.5" />
    <rect x="8" y="3" width="3" height="18" rx="1.5" />
    <rect x="14" y="6" width="3" height="12" rx="1.5" />
    <rect x="20" y="9" width="3" height="6" rx="1.5" />
  </svg>
);

const LyricsPage = () => {
  const currentSong = useAppSelector((state) => state.spotify.state?.track_window.current_track);
  const positionMs = useAppSelector((state) => state.spotify.state?.position || 0);
  const durationMs = useAppSelector((state) => state.spotify.state?.duration || 0);
  const [lyricsData, setLyricsData] = useState<LyricsPayload | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [bgColor, setBgColor] = useState('#1db954');

  // Follow lyrics setting (default true, synced to localStorage)
  const [followLyrics, setFollowLyrics] = useState<boolean>(() => {
    const saved = localStorage.getItem('lyrics_sync_enabled');
    return saved !== null ? saved === 'true' : true;
  });

  // Romanized / English script toggle (default true, synced to localStorage)
  const [showRomanized, setShowRomanized] = useState<boolean>(() => {
    const saved = localStorage.getItem('lyrics_prefer_romanized');
    return saved !== null ? saved === 'true' : true;
  });

  // Track if user manually scrolled away
  const [isUserScrolled, setIsUserScrolled] = useState(false);
  const [showSettings, setShowSettings] = useState(false);

  const containerRef = useRef<HTMLDivElement>(null);
  const lineRefs = useRef<(HTMLParagraphElement | null)[]>([]);
  const settingsRef = useRef<HTMLDivElement>(null);

  const isProgrammaticScrollRef = useRef(false);
  const programmaticScrollTimerRef = useRef<NodeJS.Timeout | null>(null);
  const scrollAnimTimerRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    if (currentSong?.album?.images?.[0]?.url) {
      getImageAnalysis2(currentSong.album.images[0].url).then((c) => {
        setBgColor(c);
      });
    } else {
      setBgColor('#1db954');
    }
  }, [currentSong?.id, currentSong?.album?.images]);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (settingsRef.current && !settingsRef.current.contains(e.target as Node)) {
        setShowSettings(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Fetch lyrics on song change
  useEffect(() => {
    if (!currentSong?.id) return;
    
    setLyricsData(null);
    setError(false);
    setIsUserScrolled(false);
    
    if (lyricsCache[currentSong.id]) {
      setLyricsData(lyricsCache[currentSong.id]);
      return;
    }

    setLoading(true);
    const controller = new AbortController();

    axios.get(`/songs/${currentSong.id}/lyrics`, { signal: controller.signal })
      .then((res: any) => {
        const data = res.data?.data;
        if (data?.lrcData && data.lrcData.length > 0) {
          const parsed: LyricLine[] = data.lrcData.map((line: any) => ({
            timeMs: line.time !== null && line.time !== undefined ? Math.floor(line.time * 1000) : null,
            text: line.text || ' ', // retain spaces for instrumentals
            romanizedText: line.romanizedText || undefined
          }));
          const result: LyricsPayload = { 
            synced: !!data.syncedLyrics && parsed.some(l => l.timeMs !== null), 
            hasRomanized: !!data.hasRomanized,
            lines: parsed 
          };
          lyricsCache[currentSong.id!] = result;
          setLyricsData(result);
        } else if (data?.lyrics) {
          const lines = data.lyrics.split('\n').map((l: string) => l.trim()).filter((l: string) => l.length > 0);
          const romanLines = data.romanizedLyrics
            ? data.romanizedLyrics.split('\n').map((l: string) => l.trim()).filter((l: string) => l.length > 0)
            : [];
          const result: LyricsPayload = { 
            synced: false, 
            hasRomanized: !!data.hasRomanized && romanLines.length > 0,
            lines: lines.map((text: string, idx: number) => ({
              timeMs: null,
              text,
              romanizedText: romanLines[idx] || undefined
            })) 
          };
          lyricsCache[currentSong.id!] = result;
          setLyricsData(result);
        } else {
          setError(true);
        }
      })
      .catch(() => {
        setError(true);
      })
      .finally(() => setLoading(false));

    return () => {
      controller.abort();
    };
  }, [currentSong?.id]);

  // Compute active lyric line
  let activeLyricIndex = -1;
  if (lyricsData?.synced && Array.isArray(lyricsData.lines)) {
    for (let i = 0; i < lyricsData.lines.length; i++) {
      const lineTime = lyricsData.lines[i].timeMs;
      if (typeof lineTime === 'number' && !isNaN(lineTime)) {
        if (lineTime <= positionMs) {
          activeLyricIndex = i;
        } else {
          break;
        }
      }
    }
  }

  // Smoothly scroll to active line
  const scrollToActiveLine = useCallback((smooth = true) => {
    if (activeLyricIndex < 0 || !containerRef.current) return;
    const activeLine = lineRefs.current[activeLyricIndex];
    const container = containerRef.current;
    if (!activeLine || !container) return;
    
    const containerHeight = container.clientHeight;
    const lineTop = activeLine.offsetTop;
    const lineHeight = activeLine.clientHeight;

    // Position the active line directly in the vertical center (middle), exactly like Spotify
    const targetTop = Math.max(0, lineTop - (containerHeight / 2) + (lineHeight / 2));

    if (scrollAnimTimerRef.current) {
      clearInterval(scrollAnimTimerRef.current);
      scrollAnimTimerRef.current = null;
    }
    if (programmaticScrollTimerRef.current) {
      clearTimeout(programmaticScrollTimerRef.current);
    }
    isProgrammaticScrollRef.current = true;

    if (!smooth) {
      container.scrollTop = targetTop;
      isProgrammaticScrollRef.current = false;
      return;
    }

    const start = container.scrollTop;
    const change = targetTop - start;
    if (Math.abs(change) < 3) {
      container.scrollTop = targetTop;
      isProgrammaticScrollRef.current = false;
      return;
    }

    const duration = 350;
    const startTime = performance.now();
    const easeInOutCubic = (t: number) =>
      t < 0.5 ? 4 * t * t * t : (t - 1) * (2 * t - 2) * (2 * t - 2) + 1;

    scrollAnimTimerRef.current = setInterval(() => {
      const elapsed = performance.now() - startTime;
      const progress = Math.min(elapsed / duration, 1);
      container.scrollTop = start + change * easeInOutCubic(progress);

      if (progress >= 1) {
        if (scrollAnimTimerRef.current) {
          clearInterval(scrollAnimTimerRef.current);
          scrollAnimTimerRef.current = null;
        }
        programmaticScrollTimerRef.current = setTimeout(() => {
          isProgrammaticScrollRef.current = false;
        }, 150);
      }
    }, 16);
  }, [activeLyricIndex]);

  // Auto-scroll when active lyric line changes, unless user manually scrolled
  useEffect(() => {
    if (!followLyrics || isUserScrolled) return;
    if (activeLyricIndex >= 0) {
      scrollToActiveLine(true);
    }
  }, [activeLyricIndex, followLyrics, isUserScrolled, scrollToActiveLine]);

  // Reset scroll to top on song change without triggering user-scrolled lock
  useEffect(() => {
    isProgrammaticScrollRef.current = true;
    if (containerRef.current) {
      containerRef.current.scrollTop = 0;
    }
    setIsUserScrolled(false);
    const timer = setTimeout(() => {
      isProgrammaticScrollRef.current = false;
    }, 150);
    return () => clearTimeout(timer);
  }, [currentSong?.id]);

  // Detect user scroll: pause auto-scroll and display floating Sync button
  const handleScroll = () => {
    if (isProgrammaticScrollRef.current) return;
    if (followLyrics && lyricsData?.synced && !isUserScrolled) {
      setIsUserScrolled(true);
    }
  };

  const handleUserInteraction = () => {
    if (scrollAnimTimerRef.current) {
      clearInterval(scrollAnimTimerRef.current);
      scrollAnimTimerRef.current = null;
    }
    isProgrammaticScrollRef.current = false;
    if (followLyrics && lyricsData?.synced && !isUserScrolled) {
      setIsUserScrolled(true);
    }
  };

  // Sync button clicked: resume auto-follow and scroll to current line
  const handleSyncClick = () => {
    setIsUserScrolled(false);
    scrollToActiveLine(true);
  };

  return (
    <div className="lyrics-page-container" style={{ backgroundColor: bgColor }}>
      <div className="lyrics-background"></div>
      
      {/* Top controls: settings and script toggle */}
      <div className="lyrics-top-controls">
        <div className="lyrics-settings" ref={settingsRef}>
          <button 
            className="lyrics-settings-btn" 
            title="Settings" 
            onClick={() => setShowSettings(!showSettings)}
          >
            <MdSettings size={22} />
          </button>
          
          {showSettings && (
            <div className="lyrics-settings-popover">
              <div className="lyrics-settings-row">
                <span>Sync lyrics to music</span>
                <label className="custom-switch">
                  <input
                    type="checkbox"
                    checked={followLyrics}
                    onChange={(e) => {
                      const checked = e.target.checked;
                      setFollowLyrics(checked);
                      localStorage.setItem('lyrics_sync_enabled', String(checked));
                      if (checked) {
                        setIsUserScrolled(false);
                      }
                    }}
                  />
                  <span className="custom-slider"></span>
                </label>
              </div>
              {lyricsData?.hasRomanized && (
                <div className="lyrics-settings-row" style={{ marginTop: '12px' }}>
                  <span>Show in English</span>
                  <label className="custom-switch">
                    <input
                      type="checkbox"
                      checked={showRomanized}
                      onChange={(e) => {
                        const checked = e.target.checked;
                        setShowRomanized(checked);
                        localStorage.setItem('lyrics_prefer_romanized', String(checked));
                      }}
                    />
                    <span className="custom-slider"></span>
                  </label>
                </div>
              )}
            </div>
          )}
        </div>

        {lyricsData?.hasRomanized && (
          <div className="lyrics-script-segmented" role="group" aria-label="Select lyrics script">
            <button
              className={`segmented-chip ${showRomanized ? 'active' : ''}`}
              onClick={() => {
                if (!showRomanized) {
                  setShowRomanized(true);
                  localStorage.setItem('lyrics_prefer_romanized', 'true');
                }
              }}
              title="English (Romanized)"
            >
              English
            </button>
            <button
              className={`segmented-chip ${!showRomanized ? 'active' : ''}`}
              onClick={() => {
                if (showRomanized) {
                  setShowRomanized(false);
                  localStorage.setItem('lyrics_prefer_romanized', 'false');
                }
              }}
              title="Original Script"
            >
              Original
            </button>
          </div>
        )}
      </div>

      {/* Floating Spotify Sync Button */}
      {followLyrics && lyricsData?.synced && isUserScrolled && (
        <button
          className="lyrics-sync-btn"
          onClick={handleSyncClick}
          title="Sync lyrics to music"
        >
          <EqualizerIcon />
          <span>Sync</span>
        </button>
      )}

      <div
        className="lyrics-content"
        ref={containerRef}
        onScroll={handleScroll}
        onWheel={handleUserInteraction}
        onTouchMove={handleUserInteraction}
      >
        {loading && <p className="lyrics-msg">Loading lyrics...</p>}
        {error && <p className="lyrics-msg">Lyrics not found for this song.</p>}
        {!loading && !error && lyricsData && (
          <div className="lyrics-list">
            {lyricsData.lines.map((line, index) => {
              const isActive = lyricsData.synced && followLyrics && index === activeLyricIndex;
              const isPassed = lyricsData.synced && followLyrics && activeLyricIndex !== -1 && index < activeLyricIndex;
              const displayText = (showRomanized && line.romanizedText) ? line.romanizedText : line.text;
              return (
                <p
                  key={index}
                  ref={el => { lineRefs.current[index] = el; }}
                  className={`lyric-line ${isActive ? 'active' : ''} ${isPassed ? 'passed' : ''}`}
                  onClick={() => {
                    if (lyricsData?.synced && typeof line.timeMs === 'number' && !isNaN(line.timeMs) && line.timeMs >= 0) {
                      playerService.seekToPosition(line.timeMs);
                      setIsUserScrolled(false);
                    }
                  }}
                  style={{ 
                    cursor: lyricsData.synced ? 'pointer' : 'default',
                    opacity: (!lyricsData.synced || !followLyrics || activeLyricIndex === -1) ? 0.9 : undefined,
                    color: (!lyricsData.synced || !followLyrics || activeLyricIndex === -1) ? '#fff' : undefined,
                    textShadow: (!lyricsData.synced || !followLyrics || activeLyricIndex === -1) ? '0 2px 4px rgba(0,0,0,0.5)' : undefined
                  }}
                >
                  {displayText}
                </p>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default LyricsPage;
